import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { VaultItem, VaultItemKind, VaultStatus } from '@pacer/shared';
import * as api from '../api';
import { ApiError } from '../api/client';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';
import { Button, Field, IconButton, Input, Select, Spinner, Textarea, cx } from './ui';
import { queryKeys } from '../state/queries';
import { confirmDelete, toast } from '../state/ui';

const KIND_ICON: Record<VaultItemKind, IconName> = { note: 'note', pointer: 'map', secret: 'key' };
const KIND_LABEL: Record<VaultItemKind, string> = { note: 'Note', pointer: 'Pointer', secret: 'Secret' };
const KIND_HINT: Record<VaultItemKind, string> = {
  note: 'Free text — instructions, wishes, whatever she needs to know.',
  pointer: 'Where something lives — which bank, where the real login is kept, who the advisor is.',
  secret: 'A value to keep hidden — a combination, a code. Shown blurred until revealed.',
};

/**
 * The encrypted vault: an "if something happens to me" store. The PIN lives only in this
 * component's memory after an unlock — it is never persisted, and it drops the moment you
 * navigate away or hit Lock. Everything reaches the server already gated by the PIN.
 */
export function Vault() {
  const { data: status, isLoading } = useQuery({ queryKey: queryKeys.vaultStatus, queryFn: api.getVaultStatus });
  const [pin, setPin] = useState<string | null>(null);
  const [items, setItems] = useState<VaultItem[]>([]);

  // Forget the PIN and decrypted contents when this view unmounts — no lingering plaintext.
  useEffect(() => () => setPin(null), []);

  const lock = () => {
    setPin(null);
    setItems([]);
  };

  const refresh = async (p: string) => {
    const { items: fresh } = await api.unlockVault(p);
    setItems(fresh);
  };

  if (isLoading) return <div className="flex justify-center py-10"><Spinner label="Opening vault" /></div>;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
        <Icon name="shield" size={16} className="text-muted" />
        <h2 className="font-display text-[20px] text-primary">Vault</h2>
        {pin !== null && (
          <Button variant="ghost" size="sm" icon="lock" className="ml-auto" onClick={lock}>
            Lock
          </Button>
        )}
      </div>

      {!status?.configured ? (
        <VaultSetup onDone={(p) => void refresh(p).then(() => setPin(p))} />
      ) : pin === null ? (
        <VaultLock status={status} onUnlock={(p) => { setPin(p); }} onUnlocked={setItems} />
      ) : (
        <VaultContents pin={pin} items={items} onRefresh={() => refresh(pin)} />
      )}
    </section>
  );
}

// --- Setup ------------------------------------------------------------------

function VaultSetup({ onDone }: { onDone: (pin: string) => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pin, setPinField] = useState('');
  const [confirm, setConfirm] = useState('');
  const [recovery, setRecovery] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (pin.length < 4) return toast('Use at least 4 characters.', 'error');
    if (pin !== confirm) return toast('The two PINs do not match.', 'error');
    setBusy(true);
    try {
      const { recoveryCode } = await api.setupVault(pin);
      await qc.invalidateQueries({ queryKey: queryKeys.vaultStatus });
      setRecovery(recoveryCode);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not create the vault.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border-subtle bg-surface/30 px-6 py-10 text-center">
        <Icon name="shield" size={26} className="text-muted" />
        <p className="max-w-md text-[14px] leading-relaxed text-secondary">
          A private, encrypted place for the things someone would need if you couldn't tell them yourself —
          where the accounts are, how to reach the advisor, what's in the safe. Encrypted so that not even
          this server can read it without your PIN.
        </p>
        <Button icon="lock" onClick={() => setOpen(true)}>Set up the vault</Button>
      </div>

      <Modal
        open={open}
        onClose={() => !busy && setOpen(false)}
        eyebrow="Vault"
        title={recovery ? 'Save your recovery code' : 'Choose a PIN'}
        footer={
          recovery ? (
            <Button icon="check" onClick={() => { setOpen(false); onDone(pin); }}>
              I've saved it — open the vault
            </Button>
          ) : (
            <Button onClick={() => void create()} disabled={busy}>
              {busy ? 'Creating…' : 'Create vault'}
            </Button>
          )
        }
      >
        {recovery ? (
          <div className="flex flex-col gap-3">
            <p className="text-[14px] leading-relaxed text-secondary">
              This is the <strong className="text-primary">only</strong> way back in if the PIN is ever lost —
              and it's how the person you're doing this for gets in without you. Write it down and give it to
              them, or seal it somewhere safe. <strong className="text-primary">It is shown once and cannot
              be recovered.</strong>
            </p>
            <button
              onClick={() => { void navigator.clipboard?.writeText(recovery); toast('Recovery code copied.', 'success'); }}
              className="tabular flex items-center justify-center gap-2 rounded-md border border-accent/40 bg-[var(--color-accent-dim)] px-4 py-4 font-mono text-[18px] tracking-wide text-primary"
            >
              {recovery}
              <Icon name="copy" size={14} className="text-muted" />
            </button>
            <p className="font-mono text-[12px] text-muted">Tap to copy. Store it apart from the PIN.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Field label="PIN or passphrase" hint="Longer is stronger. On a public URL a real passphrase beats a 4-digit PIN — you'll rarely type it.">
              <Input type="password" autoFocus value={pin} onChange={(e) => setPinField(e.target.value)} />
            </Field>
            <Field label="Confirm it">
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </Field>
          </div>
        )}
      </Modal>
    </>
  );
}

// --- Lock screen ------------------------------------------------------------

function VaultLock({
  status,
  onUnlock,
  onUnlocked,
}: {
  status: VaultStatus;
  onUnlock: (pin: string) => void;
  onUnlocked: (items: VaultItem[]) => void;
}) {
  const qc = useQueryClient();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const lockedUntil = status.lockedUntil ? new Date(status.lockedUntil) : null;
  const locked = lockedUntil && lockedUntil > new Date();

  const submit = async () => {
    if (!pin) return;
    setBusy(true);
    try {
      const { items } = await api.unlockVault(pin);
      onUnlocked(items);
      onUnlock(pin);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        await qc.invalidateQueries({ queryKey: queryKeys.vaultStatus });
        toast('Too many attempts — locked for a bit.', 'error');
      } else {
        toast('Wrong PIN.', 'error');
      }
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border border-border-subtle bg-surface px-6 py-10">
      <Icon name="lock" size={26} className="text-muted" />
      {locked ? (
        <p className="text-center font-mono text-[13px] text-danger">
          Locked until {lockedUntil!.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.
        </p>
      ) : (
        <form
          className="flex w-full max-w-xs flex-col gap-3"
          onSubmit={(e) => { e.preventDefault(); void submit(); }}
        >
          <Input
            type="password"
            autoFocus
            placeholder="Enter PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="text-center"
          />
          <Button type="submit" icon="key" disabled={busy || !pin}>
            {busy ? 'Unlocking…' : 'Unlock'}
          </Button>
        </form>
      )}
      <button onClick={() => setResetOpen(true)} className="font-mono text-[12px] text-muted underline-offset-2 hover:text-secondary hover:underline">
        Forgot the PIN? Use a recovery code
      </button>
      {resetOpen && (
        <VaultReset
          fromRecovery
          onClose={() => setResetOpen(false)}
          onReset={(p) => { setResetOpen(false); onUnlock(p); void api.unlockVault(p).then((r) => onUnlocked(r.items)); }}
        />
      )}
    </div>
  );
}

// --- Contents ---------------------------------------------------------------

function VaultContents({ pin, items, onRefresh }: { pin: string; items: VaultItem[]; onRefresh: () => Promise<void> }) {
  const [editing, setEditing] = useState<VaultItem | 'new' | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());

  const toggleReveal = (id: string) =>
    setRevealed((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const remove = async (i: VaultItem) => {
    if (!(await confirmDelete(i.title))) return;
    try {
      await api.deleteVaultItem(pin, i.id);
      await onRefresh();
    } catch {
      toast('Could not delete that item.', 'error');
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[12px] text-muted">{items.length} item{items.length === 1 ? '' : 's'} · encrypted</span>
        <button onClick={() => setResetOpen(true)} className="font-mono text-[12px] text-muted hover:text-secondary">Change PIN</button>
        <Button variant="secondary" size="sm" icon="plus" className="ml-auto" onClick={() => setEditing('new')}>
          Add
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="py-8 text-center font-mono text-[12px] text-muted">Nothing in the vault yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((i) => (
            <li key={i.id} className="group flex items-start gap-3 rounded-lg border border-border-subtle bg-surface px-3 py-2.5">
              <Icon name={KIND_ICON[i.kind]} size={15} className="mt-0.5 shrink-0 text-muted" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] text-primary">{i.title}</span>
                  <span className="font-mono text-[11px] text-muted">{KIND_LABEL[i.kind]}</span>
                </div>
                {i.body && (
                  <div className="mt-0.5 flex items-start gap-1.5">
                    <span
                      className={cx(
                        'whitespace-pre-wrap break-words text-[13px] text-secondary',
                        i.kind === 'secret' && !revealed.has(i.id) && 'select-none blur-[5px]',
                      )}
                    >
                      {i.body}
                    </span>
                    {i.kind === 'secret' && (
                      <IconButton
                        icon={revealed.has(i.id) ? 'eye-off' : 'eye'}
                        label={revealed.has(i.id) ? 'Hide' : 'Reveal'}
                        size="sm"
                        onClick={() => toggleReveal(i.id)}
                        className="shrink-0"
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <IconButton icon="settings" label="Edit" size="sm" onClick={() => setEditing(i)} />
                <IconButton icon="trash" label="Delete" size="sm" onClick={() => void remove(i)} />
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <VaultItemModal
          pin={pin}
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onRefresh(); }}
        />
      )}
      {resetOpen && <VaultReset onClose={() => setResetOpen(false)} onReset={() => setResetOpen(false)} currentPin={pin} />}
    </div>
  );
}

function VaultItemModal({
  pin,
  item,
  onClose,
  onSaved,
}: {
  pin: string;
  item: VaultItem | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState(item?.title ?? '');
  const [kind, setKind] = useState<VaultItemKind>(item?.kind ?? 'pointer');
  const [body, setBody] = useState(item?.body ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) return toast('Give it a title.', 'error');
    setBusy(true);
    try {
      const payload = { title: title.trim(), kind, body };
      if (item) await api.updateVaultItem(pin, item.id, payload);
      else await api.createVaultItem(pin, payload);
      await onSaved();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save.', 'error');
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => !busy && onClose()}
      eyebrow="Vault"
      title={item ? 'Edit item' : 'Add to vault'}
      footer={<Button onClick={() => void save()} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>}
    >
      <div className="flex flex-col gap-3">
        <Field label="Title">
          <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Fidelity brokerage" />
        </Field>
        <Field label="Type" hint={KIND_HINT[kind]}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as VaultItemKind)}>
            <option value="pointer">{KIND_LABEL.pointer}</option>
            <option value="note">{KIND_LABEL.note}</option>
            <option value="secret">{KIND_LABEL.secret}</option>
          </Select>
        </Field>
        <Field label="Details">
          <Textarea
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={kind === 'pointer' ? 'Login lives in 1Password. Advisor: Jane, 555-0142.' : ''}
          />
        </Field>
      </div>
    </Modal>
  );
}

function VaultReset({
  fromRecovery,
  currentPin,
  onClose,
  onReset,
}: {
  fromRecovery?: boolean;
  currentPin?: string;
  onClose: () => void;
  onReset: (newPin: string) => void;
}) {
  const qc = useQueryClient();
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const useRecovery = fromRecovery || !currentPin;

  const submit = async () => {
    if (newPin.length < 4) return toast('Use at least 4 characters.', 'error');
    if (newPin !== confirm) return toast('The new PINs do not match.', 'error');
    setBusy(true);
    try {
      await api.resetVault(newPin, useRecovery ? { recoveryCode } : { currentPin });
      await qc.invalidateQueries({ queryKey: queryKeys.vaultStatus });
      toast('PIN changed.', 'success');
      onReset(newPin);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not change the PIN.', 'error');
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => !busy && onClose()}
      eyebrow="Vault"
      title={useRecovery ? 'Reset with recovery code' : 'Change PIN'}
      footer={<Button onClick={() => void submit()} disabled={busy}>{busy ? 'Saving…' : 'Set new PIN'}</Button>}
    >
      <div className="flex flex-col gap-3">
        {useRecovery && (
          <Field label="Recovery code">
            <Input autoFocus value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} placeholder="XXXX-XXXX-XXXX-XXXX" className="font-mono" />
          </Field>
        )}
        <Field label="New PIN or passphrase">
          <Input type="password" autoFocus={!useRecovery} value={newPin} onChange={(e) => setNewPin(e.target.value)} />
        </Field>
        <Field label="Confirm it">
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
